import { ShoppingCart } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { cn } from "@/lib/utils";

interface CartBadgeProps {
  className?: string;
  onClick?: () => void;
  variant?: "default" | "mobile" | "compact";
  showOnlyWhenItems?: boolean; // Nueva prop para controlar visibilidad
}

export function CartBadge({
  className,
  onClick,
  variant = "default",
  showOnlyWhenItems = true, // Por defecto solo mostrar cuando hay items
}: CartBadgeProps) {
  const totalItems = useCartStore((state) => state.getTotalItems());

  // Si showOnlyWhenItems es true y no hay items, no renderizar nada
  if (showOnlyWhenItems && totalItems === 0) {
    return null;
  }

  // Configuración según la variante
  const getVariantStyles = () => {
    switch (variant) {
      case "mobile":
        return {
          button: "h-12 w-12 p-3", // Más grande para mobile
          icon: "h-6 w-6",
          badge: "min-w-[24px] h-6 px-1.5 text-sm",
        };
      case "compact":
        return {
          button: "h-8 w-8 p-1.5",
          icon: "h-4 w-4",
          badge: "min-w-[18px] h-4.5 px-1 text-xs",
        };
      default:
        return {
          button: "h-10 w-10 p-2",
          icon: "h-5 w-5",
          badge: "min-w-[20px] h-5 px-1 text-xs",
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <button
      onClick={onClick}
      className={cn(
        "relative inline-flex items-center justify-center rounded-full",
        "bg-primary text-primary-foreground hover:bg-primary/90",
        "transition-all duration-200 ease-in-out",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        "active:scale-95", // Feedback táctil
        "shadow-sm hover:shadow-md", // Sombras temáticas
        styles.button,
        className
      )}
      aria-label={`Carrito con ${totalItems} productos`}
    >
      <ShoppingCart
        className={cn("transition-transform duration-200", styles.icon)}
      />

      {totalItems > 0 && (
        <span
          className={cn(
            "absolute -top-1 -right-1",
            "bg-destructive text-destructive-foreground",
            "font-bold rounded-full",
            "flex items-center justify-center",
            "animate-in zoom-in-50 duration-200",
            "border-2 border-background",
            "shadow-sm", // Sombra temática
            styles.badge
          )}
          aria-label={`${totalItems} productos en el carrito`}
        >
          {totalItems > 99 ? "99+" : totalItems}
        </span>
      )}

      {/* Indicador visual adicional para mobile */}
      {variant === "mobile" && totalItems > 0 && (
        <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
      )}
    </button>
  );
}

// Variante específica para mobile con mejor feedback visual
export function MobileCartBadge({
  className,
  onClick,
  showOnlyWhenItems,
}: Omit<CartBadgeProps, "variant">) {
  return (
    <CartBadge
      variant="mobile"
      className={className}
      onClick={onClick}
      showOnlyWhenItems={showOnlyWhenItems}
    />
  );
}

// Variante compacta para espacios reducidos
export function CompactCartBadge({
  className,
  onClick,
  showOnlyWhenItems,
}: Omit<CartBadgeProps, "variant">) {
  return (
    <CartBadge
      variant="compact"
      className={className}
      onClick={onClick}
      showOnlyWhenItems={showOnlyWhenItems}
    />
  );
}
